import {
  Container,
  Typography,
  Paper,
  Select,
  FormControl,
  MenuItem,
  Button,
  InputLabel,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Checkbox,
  Box,
  Snackbar,
  IconButton,
  CircularProgress,
  Fade,
  Divider,
  Modal,
} from "@mui/material";
import { Close, Info as InfoIcon } from "@mui/icons-material";
import React, { useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import base64 from "base64-js";


const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  // borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ClassTeacherMapping = () => {
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [ddlClass, setddlClass] = useState([]);
  const [ddlSection, setddlSection] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [classTeachers, setClassTeachers] = useState([]);
  const [teachersList, setTeacherList] = useState([]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  const fetchddlClass = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok)
        throw new Error(`Error fetching class names: ${response.status}`);
      const data = await response.json();

      // Ensure we always set an array
      setddlClass(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error(error);
      setddlClass([]); // Always set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchddlSection = useCallback(async (classId) => {
    try {
      setLoading(true);
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/AssignmentCreate/ddlSection_clsId`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ teacherId: 0, classId: classId }),
      });
      if (!response.ok)
        throw new Error(`Error fetching sections: ${response.status}`);
      const data = await response.json();
      setddlSection(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClassTeachers = useCallback(async (classId, sectionId) => {
    try {
      setLoading(true);
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/ClassTeacherMapping/FetchClassTeacher`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId: classId, sectionId: sectionId }),
      });
      if (!response.ok)
        throw new Error(`Error fetching class teachers: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        setClassTeachers([]);
        setSelectedTeacherIds({});
        return;
      }
      setClassTeachers(data);

      // Initialize selectedTeacherIds with existing assignments
      const initialTeacherIds = {};
      data.forEach((teacher) => {
        if (teacher.isChecked === 1) {
          initialTeacherIds[teacher.subjectId] = teacher.teacherId;
        }
      });
      setSelectedTeacherIds(initialTeacherIds);
    } catch (error) {
      console.error(error);
      setClassTeachers([]);
      setSelectedTeacherIds({});
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/AssignmentCreate/GetTeacher`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok)
        throw new Error(`Error fetching class teachers: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === null) {
        setTeacherList([]);
        return;
      }
      setTeacherList(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchddlClass();
    fetchTeachers();
    Authorizer();
  }, [fetchddlClass, fetchTeachers]);

  const handleClassChange = (event) => {
    const classId = event.target.value;
    setSelectedClassId(classId);
    setSelectedSectionId("");
    setClassTeachers([]);
    setSelectedTeacherIds({});
    fetchddlSection(classId);
  };

  useEffect(() => {
    if (selectedSectionId !== "") {
      fetchClassTeachers(selectedClassId, selectedSectionId);
    }
  }, [selectedSectionId, fetchClassTeachers]);

  const handleTeacherChange = (event, subjectId) => {
    const teacherId = event.target.value;
    setSelectedTeacherIds((prevState) => ({
      ...prevState,
      [subjectId]: teacherId || undefined,
    }));
  };

  const handleCheckboxChange = (subjectId) => {
    setSelectedTeacherIds((prevState) => {
      const newState = { ...prevState };
      if (newState[subjectId] !== undefined) {
        delete newState[subjectId]; // Uncheck: remove the teacher ID
      } else {
        // Check: set to empty string if no teacher assigned yet, or use existing teacherId
        const teacher = classTeachers.find((t) => t.subjectId === subjectId);
        newState[subjectId] = teacher?.isChecked === 1 ? teacher.teacherId : "";
      }
      return newState;
    });
  };

  const handleSave = async () => {
    const isChecked = Object.values(selectedTeacherIds).some(
      (id) => id !== undefined && id !== ""
    );
    if (!isChecked) {
      setIsSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/ClassTeacherMapping`;
      const token = sessionStorage.getItem("token");

      const selectedRows = classTeachers
        .map((teacher) => ({
          classId: selectedClassId,
          sectionId: selectedSectionId,
          subjectId: teacher.subjectId,
          teacherId: selectedTeacherIds[teacher.subjectId] || 0,
        }))
        .filter((row) => row.teacherId !== 0); // Only send rows with a valid teacherId

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(selectedRows),
      });
      if (!response.ok)
        throw new Error(`Error saving class teachers: ${response.status}`);
      setSuccessMessage("Class teachers saved successfully");
      // Refetch to ensure UI reflects the latest state
      await fetchClassTeachers(selectedClassId, selectedSectionId);
    } catch (error) {
      console.error("API request error:", error);
      setErrorMessage("Failed to save class teachers");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessAlert = () => setSuccessMessage("");
  const handleCloseErrorAlert = () => setErrorMessage("");
  const handleSnackbarClose = () => setIsSnackbarOpen(false);

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Class Teacher Mapping page allows authorized staff to assign teachers to subjects for a specific class and section. Select a class and section to load the subject list, then assign a teacher to each subject and click SAVE.",
    },
    {
      title: "🎓 How to Use",
      items: [
        "Step 1 – Select a Class from the Class dropdown.",
        "Step 2 – Select a Section from the Section dropdown (enabled after selecting a class).",
        "Step 3 – The subject list loads automatically. Check the checkbox next to each subject you want to assign a teacher to.",
        "Step 4 – Use the Teacher dropdown in each row to select the teacher for that subject.",
        "Step 5 – Click SAVE to save all the teacher assignments.",
      ],
    },
    {
      title: "📊 Table Columns",
      items: [
        "Select – Checkbox to include or exclude a subject from the mapping. The Teacher dropdown is enabled only when checked.",
        "Serial Number – Row number in the list.",
        "Subject Name – The name of the subject for the selected class.",
        "Teacher – Dropdown to assign a teacher to the subject. Only active when the row is checked.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "SAVE – Saves all checked subject-teacher assignments for the selected class and section. At least one teacher must be selected. Requires Add permission.",
      ],
    },
    {
      title: "🔐 Permissions",
      content:
        "The SAVE button is disabled if you do not have Add permission. Contact your administrator if you need access.",
    },
  ];

  return (
    <Container maxWidth="md" sx={{ mb: 2 }}>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "900px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          Class Teacher Mapping
        </Typography>

        {/* Info Icon Button */}
        <Tooltip title="View page information & guide" arrow>
          <IconButton
            onClick={() => setShowInfoModal(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 30,
              height: 30,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(100,181,246,0.25)",
                borderColor: "#64B5F6",
                color: "#64B5F6",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <StyledPaper elevation={3}>
        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <FormControl sx={{ minWidth: 350 }}>
            <InputLabel>Class</InputLabel>
            <Select
              id="class"
              label="Class"
              value={selectedClassId}
              onChange={handleClassChange}
              disabled={loading}
              // sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">Select Class</MenuItem>
              {Array.isArray(ddlClass) && ddlClass.map((item) => (
                <MenuItem key={item.classId} value={item.classId}>
                  {item.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 350 }}>
            <InputLabel>Section</InputLabel>
            <Select
              id="section"
              label="Section"
              value={selectedSectionId}
              onChange={(e) => setSelectedSectionId(e.target.value)}
              disabled={loading || !selectedClassId}
              // sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">Select Section</MenuItem>
              {ddlSection.map((item) => (
                <MenuItem key={item.sectionId} value={item.sectionId}>
                  {item.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </StyledPaper>

      {selectedSectionId && (
        <Fade in={!!selectedSectionId}>
          <StyledPaper elevation={3} sx={{ mt: 3 }}>
            <TableContainer component={Paper}>
              <Table
                sx={{
                  minWidth: 650,
                  border: '1px solid #ccc',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #ccc',
                    fontSize: '1rem',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Select
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Serial Number
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Subject Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Teacher
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classTeachers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body1" color="text.secondary">
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    classTeachers.map((teacher, index) => (
                      <TableRow
                        key={teacher.subjectId}
                        sx={{
                          borderTop: '1px solid #ccc',
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            transition: "background-color 0.2s",
                          },
                        }}
                      >
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          <Checkbox
                            checked={selectedTeacherIds[teacher.subjectId] !== undefined}
                            onChange={() => handleCheckboxChange(teacher.subjectId)}
                            disabled={loading}
                            sx={{ "&.Mui-checked": { color: "#1976d2" } }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          {teacher.subjectName}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          <FormControl fullWidth>
                            <InputLabel>Teacher</InputLabel>
                            <Select
                              value={selectedTeacherIds[teacher.subjectId] || ""}
                              label="Teacher"
                              onChange={(e) => handleTeacherChange(e, teacher.subjectId)}
                              disabled={loading || selectedTeacherIds[teacher.subjectId] === undefined}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="">Select Teacher</MenuItem>
                              {teachersList.map((item) => (
                                <MenuItem key={item.employeeId} value={item.employeeId}>
                                  {item.employeeName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Tooltip title={!canAdd && "You are not Authorized to make changes"} arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSave}
                    disabled={loading || !canAdd}
                  >
                    {loading ? "SAVING..." : "SAVE"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </StyledPaper>
        </Fade>
      )}

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 560 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Class Teacher Mapping — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
           
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={successMessage !== ""}
        autoHideDuration={6000}
        onClose={handleCloseSuccessAlert}
        message={successMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseSuccessAlert}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
      <Snackbar
        open={errorMessage !== ""}
        autoHideDuration={6000}
        onClose={handleCloseErrorAlert}
        message={errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseErrorAlert}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Please select at least one teacher"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Container>
  );
};

export default ClassTeacherMapping;